import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import Spinner from '../components/Spinner';
import IconButton from '../components/IconButton';
import DataTable from '../components/DataTable';
import './Tags.css';

function Equipment({ tenantId }) {
  const { showNotification } = useNotification();
  const [equipment, setEquipment] = useState([]);
  const [carMakes, setCarMakes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    make: '',
    color: '#4ECDC4'
  });

  useEffect(() => {
    if (tenantId) {
      loadEquipment();
      loadCarMakes();
    }
  }, [tenantId]);

  const loadEquipment = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get('/equipment', tenantId);
      setEquipment(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Sonderaustattungen');
      showNotification(err.message || 'Fehler beim Laden der Sonderaustattungen', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCarMakes = async () => {
    try {
      const cars = await api.get('/cars', tenantId);
      const makes = [...new Set(cars.map(car => car.make).filter(Boolean))].sort();
      setCarMakes(makes);
    } catch (error) {
      console.error('Error loading car makes:', error);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post('/equipment', {
        name: formData.name.trim(),
        make: formData.make || null,
        color: formData.color
      }, tenantId);
      showNotification('Sonderaustattung erfolgreich erstellt', 'success');
      setShowCreateModal(false);
      setFormData({ name: '', make: '', color: '#4ECDC4' });
      loadEquipment();
      loadCarMakes();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Fehler beim Erstellen der Sonderaustattung';
      setError(errorMsg);
      showNotification(errorMsg, 'error');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api.put(`/equipment/${editingEquipment.id}`, {
        name: formData.name.trim(),
        make: formData.make || null,
        color: formData.color
      }, tenantId);
      showNotification('Sonderaustattung erfolgreich aktualisiert', 'success');
      setEditingEquipment(null);
      setFormData({ name: '', make: '', color: '#4ECDC4' });
      loadEquipment();
      loadCarMakes();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Fehler beim Aktualisieren der Sonderaustattung';
      setError(errorMsg);
      showNotification(errorMsg, 'error');
    }
  };

  const handleDelete = async (equipmentId) => {
    if (!window.confirm('Möchten Sie diese Sonderaustattung wirklich löschen?')) {
      return;
    }
    setError(null);
    try {
      await api.delete(`/equipment/${equipmentId}`, tenantId);
      showNotification('Sonderaustattung erfolgreich gelöscht', 'success');
      loadEquipment();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message || 'Fehler beim Löschen der Sonderaustattung';
      setError(errorMsg);
      showNotification(errorMsg, 'error');
    }
  };

  const handleEdit = (equipment) => {
    setEditingEquipment(equipment);
    setFormData({
      name: equipment.name,
      make: equipment.make || '',
      color: equipment.color || '#4ECDC4'
    });
    setShowCreateModal(true);
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setEditingEquipment(null);
    setFormData({ name: '', make: '', color: '#4ECDC4' });
  };

  const presetColors = [
    '#4ECDC4', '#45B7D1', '#FFA07A', '#FFD93D', '#C77DFF',
    '#6BCF7F', '#FF8C94', '#9B8DFF', '#FFB84D', '#FF6B6B'
  ];

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="tags-page">
      <div className="page-header">
        <h1>Sonderaustattungen</h1>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <IconButton icon="add" /> Neue Sonderaustattung
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <DataTable
        data={equipment}
        columns={[
          {
            key: 'actions',
            header: '',
            sortable: false,
            width: '100px',
            render: (row) => (
              <div style={{ display: 'inline-flex', gap: '0.25rem', whiteSpace: 'nowrap' }}>
                <IconButton
                  icon="edit"
                  onClick={() => handleEdit(row)}
                  title="Bearbeiten"
                />
                <IconButton
                  icon="delete"
                  onClick={() => handleDelete(row.id)}
                  title="Löschen"
                  className="danger"
                />
              </div>
            )
          },
          {
            key: 'color',
            header: 'Farbe',
            sortable: false,
            width: '80px',
            render: (row) => (
              <div 
                style={{ 
                  width: '32px', 
                  height: '32px', 
                  borderRadius: '6px', 
                  backgroundColor: row.color || '#4ECDC4',
                  border: '2px solid rgba(0, 0, 0, 0.1)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}
                title={row.color?.toUpperCase() || '#4ECDC4'}
              />
            )
          },
          {
            key: 'name',
            header: 'Name',
            sortType: 'string',
            accessor: (row) => row.name || ''
          },
          {
            key: 'make',
            header: 'Marke',
            sortType: 'string',
            accessor: (row) => row.make || 'Alle Marken',
            render: (row) => (
              <span style={{ color: row.make ? '#333' : '#999' }}>
                {row.make || 'Alle Marken'}
              </span>
            )
          },
          {
            key: 'colorCode',
            header: 'Farbcode',
            sortType: 'string',
            accessor: (row) => row.color?.toUpperCase() || '#4ECDC4',
            render: (row) => (
              <span style={{ fontFamily: 'monospace', fontSize: '0.9rem', color: '#64748b' }}>
                {row.color?.toUpperCase() || '#4ECDC4'}
              </span>
            )
          }
        ]}
        defaultSort={{ key: 'name', direction: 'asc' }}
        emptyMessage="Keine Sonderaustattungen vorhanden"
      />

      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>{editingEquipment ? 'Sonderaustattung bearbeiten' : 'Neue Sonderaustattung erstellen'}</h2>
            <form onSubmit={editingEquipment ? handleUpdate : handleCreate}>
              <div className="form-group">
                <label htmlFor="equipmentName">Name *</label>
                <input
                  type="text"
                  id="equipmentName"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="z.B. Navigationssystem, Sitzheizung"
                />
              </div>

              <div className="form-group">
                <label htmlFor="equipmentMake">Marke (optional)</label>
                <select
                  id="equipmentMake"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                >
                  <option value="">Alle Marken</option>
                  {carMakes.map(make => (
                    <option key={make} value={make}>{make}</option>
                  ))}
                </select>
                <p style={{ fontSize: '0.875rem', color: '#666', marginTop: '0.5rem' }}>
                  Wenn "Alle Marken" ausgewählt, gilt die Sonderaustattung für alle Marken
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="equipmentColor">Farbe</label>
                <div className="color-input-group">
                  <input
                    type="color"
                    id="equipmentColor"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    required
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => {
                      if (/^#[0-9A-Fa-f]{6}$/i.test(e.target.value)) {
                        setFormData({ ...formData, color: e.target.value.toUpperCase() });
                      }
                    }}
                    placeholder="#4ECDC4"
                    pattern="^#[0-9A-Fa-f]{6}$"
                  />
                </div>
                <div className="preset-colors">
                  {presetColors.map(color => (
                    <button
                      key={color}
                      type="button"
                      className="preset-color-btn"
                      style={{ backgroundColor: color }}
                      onClick={() => setFormData({ ...formData, color })}
                      title={color}
                    />
                  ))}
                </div>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingEquipment ? 'Aktualisieren' : 'Erstellen'}
                </button>
                <button type="button" className="btn-secondary" onClick={handleCancel}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Equipment;
